import type {
  ApiError,
  HistogramData,
  InstanceTimeframe,
  LogRecord,
  Metadata,
  Metric,
  MetricMetadata,
  ServiceWithInstances,
  Span,
  Trace,
  TraceSummary,
} from "./types";
import {
  isArrayOf,
  isArrayOfOrNull,
  isBoolean,
  isNullable,
  isNumber,
  isRecord,
  isString,
  type TypeGuard,
} from "../types/common";

export const isApiError: TypeGuard<ApiError> = (value): value is ApiError =>
  isRecord(value) && isString(value.error);

export const isSpan: TypeGuard<Span> = (value): value is Span => {
  if (!isRecord(value)) return false;
  return (
    isString(value.trace_id) &&
    isString(value.span_id) &&
    isNullable(isString)(value.parent_span_id) &&
    isString(value.service_name) &&
    isNullable(isString)(value.service_instance) &&
    isString(value.span_name) &&
    isNumber(value.kind) &&
    isString(value.start_time) &&
    isString(value.end_time) &&
    isNumber(value.status_code) &&
    isNullable(isString)(value.status_message) &&
    isRecord(value.resource_attrs) &&
    isRecord(value.span_attrs) &&
    isNumber(value.events_count)
  );
};

export const isTrace: TypeGuard<Trace> = (value): value is Trace => {
  if (!isRecord(value)) return false;
  return isString(value.trace_id) && isArrayOf(isSpan)(value.spans);
};

export const isTraceSummary: TypeGuard<TraceSummary> = (value): value is TraceSummary => {
  if (!isRecord(value)) return false;
  return (
    isString(value.trace_id) &&
    isString(value.service_name) &&
    isNullable(isString)(value.service_instance) &&
    isString(value.start_time) &&
    isString(value.end_time) &&
    isNumber(value.span_count)
  );
};

export const isLogRecord: TypeGuard<LogRecord> = (value): value is LogRecord => {
  if (!isRecord(value)) return false;
  return (
    isString(value.ts) &&
    isNullable(isString)(value.observed_ts) &&
    isString(value.service_name) &&
    isNullable(isString)(value.service_instance) &&
    isNumber(value.severity_number) &&
    isString(value.severity_text) &&
    isString(value.body) &&
    isNullable(isString)(value.trace_id) &&
    isNullable(isString)(value.span_id) &&
    isRecord(value.resource_attrs) &&
    isRecord(value.log_attrs)
  );
};

export const isHistogramData: TypeGuard<HistogramData> = (value): value is HistogramData => {
  if (!isRecord(value)) return false;
  return (
    isNumber(value.count) &&
    isNumber(value.sum) &&
    isArrayOf(isNumber)(value.bucket_counts) &&
    isArrayOf(isNumber)(value.explicit_bounds)
  );
};

export const isMetric: TypeGuard<Metric> = (value): value is Metric => {
  if (!isRecord(value)) return false;

  const isMetricType = (v: unknown): v is 0 | 1 | 2 => v === 0 || v === 1 || v === 2;

  const isUnit = (v: unknown): v is string | undefined => v === undefined || isString(v);

  const isNumberOrNull = (v: unknown): v is number | null => v === null || isNumber(v);

  const isHistogramOrNull = (v: unknown): v is HistogramData | null =>
    v === null || isHistogramData(v);

  return (
    isString(value.ts) &&
    isString(value.service_name) &&
    isNullable(isString)(value.service_instance) &&
    isString(value.metric_name) &&
    isUnit(value.unit) &&
    isMetricType(value.type) &&
    isNumberOrNull(value.value) &&
    isHistogramOrNull(value.histogram_data) &&
    isRecord(value.attrs) &&
    isRecord(value.resource_attrs)
  );
};

export const isMetricMetadata: TypeGuard<MetricMetadata> = (value): value is MetricMetadata => {
  if (!isRecord(value)) return false;
  const isMetricType = (v: unknown): v is 0 | 1 | 2 => v === 0 || v === 1 || v === 2;
  return isString(value.metric_name) && isMetricType(value.type) && isString(value.unit);
};

export const isInstanceTimeframe: TypeGuard<InstanceTimeframe> = (
  value,
): value is InstanceTimeframe => {
  if (!isRecord(value)) return false;
  return (
    isString(value.instance_id) &&
    isString(value.first_seen) &&
    isString(value.last_seen)
  );
};

export const isServiceWithInstances: TypeGuard<ServiceWithInstances> = (
  value,
): value is ServiceWithInstances => {
  if (!isRecord(value)) return false;
  return (
    isString(value.service_name) && isArrayOf(isInstanceTimeframe)(value.instances)
  );
};

export const isMetadata: TypeGuard<Metadata> = (value): value is Metadata => {
  if (!isRecord(value)) return false;
  return (
    isArrayOfOrNull(isMetricMetadata)(value.metrics) &&
    isArrayOfOrNull(isServiceWithInstances)(value.services)
  );
};

// --- “top-level” endpoint shapes ---

export const isTraceSummaryArrayOrNull: TypeGuard<TraceSummary[] | null> = (
  value,
): value is TraceSummary[] | null => value === null || isArrayOf(isTraceSummary)(value);

export const isSpanArrayOrNull: TypeGuard<Span[] | null> = (value): value is Span[] | null =>
  value === null || isArrayOf(isSpan)(value);

export const isLogRecordArrayOrNull: TypeGuard<LogRecord[] | null> = (
  value,
): value is LogRecord[] | null => value === null || isArrayOf(isLogRecord)(value);

export const isMetricArrayOrNull: TypeGuard<Metric[] | null> = (value): value is Metric[] | null =>
  value === null || isArrayOf(isMetric)(value);
